import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  challengeThemeStyle,
  type ChallengeMission,
  type ChallengeSubmission,
} from '../../types/assignmentChallenge';
import {
  fetchChallengeBundle,
  fetchChallengeSubmissionsForItem,
  formatChallengeError,
  getChallengePhotoCardUrls,
  getChallengePhotoFullUrls,
  recoverChallengePhotoUrl,
  removeChallengePhotos,
  resetChallengeItemSubmissions,
} from '../../services/assignmentChallengeService';

type MemberRef = { id: string; name?: string };

type Props = {
  projectId: string;
  subTaskId: string;
  subTaskTitle: string;
  members: MemberRef[];
  /** 과제 체크 모달과 같은 학생 블럭(상태 색·선택·포인트) */
  renderHead: (memberId: string, extraSide: React.ReactNode) => React.ReactNode;
  /** 학생 블럭과 같은 과제 상태 색(카드 테두리) */
  statusColorOf: (memberId: string) => string;
  memos: Record<string, string>;
  onMemoChange: (memberId: string, value: string) => void;
  displayName: (memberId: string) => string;
  /** 기록 초기화 후 이 챌린지의 과제 상태를 미확인으로 되돌림 */
  onReset: () => Promise<void> | void;
  themeColor?: string;
};

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(
    d.getMinutes()
  ).padStart(2, '0')}`;
}

const CloseIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
  </svg>
);

const ChallengeCheckStudentList: React.FC<Props> = ({
  projectId,
  subTaskId,
  subTaskTitle,
  members,
  renderHead,
  memos,
  onMemoChange,
  displayName,
  statusColorOf,
  onReset,
  themeColor,
}) => {
  const themeStyle = challengeThemeStyle(themeColor);
  const [subs, setSubs] = useState<ChallengeSubmission[]>([]);
  const [missionsFallback, setMissionsFallback] = useState<ChallengeMission[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [fullUrls, setFullUrls] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'submitted' | 'unsubmitted'>('all');
  const [detail, setDetail] = useState<{ sub: ChallengeSubmission; name: string } | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const recoveredRef = useRef<Set<string>>(new Set());

  const onCardImageError = (path: string) => {
    if (recoveredRef.current.has(path)) {
      setPhotoUrls((prev) => ({ ...prev, [path]: '' }));
      return;
    }
    recoveredRef.current.add(path);
    void recoverChallengePhotoUrl(path)
      .then((url) => setPhotoUrls((prev) => ({ ...prev, [path]: url ?? '' })))
      .catch(() => setPhotoUrls((prev) => ({ ...prev, [path]: '' })));
  };

  const load = useCallback(async (force = false) => {
    if (!projectId || !subTaskId) return;
    setLoading(true);
    setError('');
    try {
      setSubs(await fetchChallengeSubmissionsForItem(projectId, subTaskId, { force, onCached: setSubs }));
    } catch (e) {
      setError(formatChallengeError(e));
    } finally {
      setLoading(false);
    }
  }, [projectId, subTaskId]);

  useEffect(() => {
    setSubs([]);
    setDetail(null);
    void load();
  }, [load]);

  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    const apply = (items: Array<{ id: string; content: { missions: ChallengeMission[] } }>) => {
      if (!cancelled) setMissionsFallback(items.find((i) => i.id === subTaskId)?.content.missions ?? []);
    };
    void fetchChallengeBundle(projectId, { onCached: (b) => apply(b.items) })
      .then((b) => b && apply(b.items))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId, subTaskId]);

  useEffect(() => {
    if (!projectId) return;
    void removeChallengePhotos(projectId, { staleOnly: true }).catch(() => undefined);
  }, [projectId]);

  useEffect(() => {
    const paths = subs.flatMap((s) => s.answers.map((a) => a.photoPath).filter((p): p is string => !!p));
    if (paths.length === 0) return;
    let cancelled = false;
    void getChallengePhotoCardUrls(paths)
      .then((urls) => {
        if (!cancelled) setPhotoUrls((prev) => ({ ...prev, ...urls }));
      })
      .catch((e) => {
        if (!cancelled) setError(formatChallengeError(e));
      });
    return () => {
      cancelled = true;
    };
  }, [subs]);

  useEffect(() => {
    const paths = detail?.sub.answers.map((a) => a.photoPath).filter((p): p is string => !!p) ?? [];
    if (paths.length === 0) return undefined;
    let cancelled = false;
    void getChallengePhotoFullUrls(paths)
      .then((urls) => {
        if (!cancelled) setFullUrls((prev) => ({ ...prev, ...urls }));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [detail]);

  useEffect(() => {
    if (!detail && !resetOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      e.preventDefault();
      if (resetOpen) setResetOpen(false);
      else setDetail(null);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [detail, resetOpen]);

  const subByMember = useMemo(() => new Map(subs.map((s) => [s.memberId, s])), [subs]);
  const visible =
    statusFilter === 'all'
      ? members
      : members.filter((m) => subByMember.has(m.id) === (statusFilter === 'submitted'));
  const submittedCount = members.filter((m) => subByMember.has(m.id)).length;
  const toggleFilter = (next: 'submitted' | 'unsubmitted') =>
    setStatusFilter((prev) => (prev === next ? 'all' : next));

  const missionsOf = (sub: ChallengeSubmission) =>
    sub.itemSnapshot.missions.length > 0 ? sub.itemSnapshot.missions : missionsFallback;

  const focusNextMemo = (memberId: string) => {
    const inputs = Array.from(
      listRef.current?.querySelectorAll<HTMLInputElement>('input[data-chl-memo-id]') ?? []
    );
    const idx = inputs.findIndex((el) => el.getAttribute('data-chl-memo-id') === memberId);
    if (idx >= 0 && idx + 1 < inputs.length) window.setTimeout(() => inputs[idx + 1].focus(), 0);
  };

  const reset = async () => {
    if (resetText.trim() !== '초기화') return;
    setBusy(true);
    setError('');
    try {
      await resetChallengeItemSubmissions(projectId, subTaskId);
      setResetOpen(false);
      setResetText('');
      setDetail(null);
      await load(true);
      await onReset();
    } catch (e) {
      setError(`${formatChallengeError(e)} 다시 시도하면 남은 항목을 이어서 정리합니다.`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="cp-chl-check" ref={listRef} style={themeStyle}>
      <div className="cp-chl-check-bar">
        <span className="cp-chl-check-count">
          제출 {submittedCount}/{members.length}명
        </span>
        <div className="cp-chl-subs-filter" role="group" aria-label="제출 상태로 보기">
          <label className={`cp-chl-filter-chip${statusFilter === 'submitted' ? ' is-on' : ''}`}>
            <input
              type="checkbox"
              checked={statusFilter === 'submitted'}
              onChange={() => toggleFilter('submitted')}
            />
            <span className="cp-chl-filter-dot" aria-hidden />
            제출 학생 보기
            <span className="cp-chl-filter-num">{submittedCount}</span>
          </label>
          <label className={`cp-chl-filter-chip${statusFilter === 'unsubmitted' ? ' is-on' : ''}`}>
            <input
              type="checkbox"
              checked={statusFilter === 'unsubmitted'}
              onChange={() => toggleFilter('unsubmitted')}
            />
            <span className="cp-chl-filter-dot" aria-hidden />
            미제출 학생 보기
            <span className="cp-chl-filter-num">{members.length - submittedCount}</span>
          </label>
        </div>
        <button
          type="button"
          className={`cp-chl-bar-btn${loading ? ' is-loading' : ''}`}
          onClick={() => void load()}
          disabled={loading}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
            <path
              d="M20 11a8 8 0 1 0-2.34 5.66M20 4v7h-7"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          {loading ? '불러오는 중…' : '제출 새로고침'}
        </button>
        <button
          type="button"
          className="cp-chl-bar-btn is-danger"
          onClick={() => setResetOpen(true)}
          disabled={busy || subs.length === 0}
        >
          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
            <path
              d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13M10 11v6M14 11v6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          기록 초기화
        </button>
      </div>
      {error ? (
        <p className="cp-chl-subs-error" role="alert">
          {error}
        </p>
      ) : null}

      <ul className="cp-chl-check-list">
        {members.length === 0 ? <li className="cp-chl-subs-empty">배정된 멤버가 없습니다.</li> : null}
        {members.length > 0 && visible.length === 0 ? (
          <li className="cp-chl-subs-empty">
            {statusFilter === 'unsubmitted' ? '모든 학생이 제출했어요.' : '아직 제출한 학생이 없어요.'}
          </li>
        ) : null}
        {visible.map((m) => {
          const sub = subByMember.get(m.id);
          const name = displayName(m.id);
          return (
            <li
              key={m.id}
              className={`cp-chl-check-card${sub ? ' has-sub' : ''}`}
              style={{ ['--cp-chl-card-status' as string]: statusColorOf(m.id) } as React.CSSProperties}
            >
              {renderHead(
                m.id,
                <span className="cp-chl-check-time">
                  {sub ? `제출 ${formatDateTime(sub.submittedAt)}` : '미제출'}
                </span>
              )}
              {sub ? (
                <ol
                  className="cp-chl-subs-strip cp-chl-check-strip"
                  aria-label={`${name} 제출 내용 (누르면 자세히 보기)`}
                  onClick={() => setDetail({ sub, name })}
                >
                  {missionsOf(sub).map((ms, i) => {
                    const ans = sub.answers.find((a) => a.missionId === ms.id);
                    const url = ans?.photoPath ? photoUrls[ans.photoPath] : undefined;
                    const empty = !ans?.photoPath && !ans?.text;
                    return (
                      <li key={ms.id} className={`cp-chl-subs-answer${empty ? ' is-empty' : ''}`}>
                        <h4 className="cp-chl-subs-answer-title">
                          {i + 1}. {ms.title || '인증 항목'}
                        </h4>
                        {ans?.photoPath ? (
                          url ? (
                            <div className="cp-chl-subs-answer-photo">
                              <img
                                src={url}
                                alt=""
                                loading="lazy"
                                onError={() => onCardImageError(ans.photoPath as string)}
                              />
                            </div>
                          ) : (
                            <div className="cp-chl-subs-answer-photo is-loading">
                              {url === '' ? '사진을 불러오지 못했어요' : '사진 불러오는 중…'}
                            </div>
                          )
                        ) : null}
                        {ans?.text ? <p className="cp-chl-subs-answer-text">{ans.text}</p> : null}
                        {empty ? <p className="cp-chl-subs-answer-empty">미제출</p> : null}
                      </li>
                    );
                  })}
                </ol>
              ) : (
                <p className="cp-chl-subs-card-empty">아직 제출하지 않았어요.</p>
              )}
              <div className="cp-chl-check-memo">
                <span className="cp-chl-check-memo-label">메모</span>
                <input
                  type="text"
                  data-chl-memo-id={m.id}
                  value={memos[m.id] ?? ''}
                  placeholder={`${name} 메모 입력`}
                  onChange={(e) => onMemoChange(m.id, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' || e.nativeEvent.isComposing) return;
                    e.preventDefault();
                    focusNextMemo(m.id);
                  }}
                />
              </div>
            </li>
          );
        })}
      </ul>

      {detail
        ? createPortal(
            <div
              className="cp-modal-backdrop cp-chl-subs-detail-backdrop"
              role="presentation"
              onClick={() => setDetail(null)}
              style={themeStyle}
            >
              <div
                className="cp-modal cp-chl-subs-detail"
                role="dialog"
                aria-modal="true"
                aria-label={`${detail.name} 제출 내용`}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="cp-chl-subs-detail-head">
                  <h3>
                    {detail.name} · {detail.sub.itemSnapshot.title || subTaskTitle}
                  </h3>
                  <button type="button" className="cp-chl-subs-close" onClick={() => setDetail(null)} aria-label="닫기">
                    <CloseIcon />
                  </button>
                </div>
                <p className="cp-chl-subs-detail-time">제출 {formatDateTime(detail.sub.submittedAt)}</p>
                <ol className="cp-chl-subs-answers">
                  {missionsOf(detail.sub).map((ms, i) => {
                    const ans = detail.sub.answers.find((a) => a.missionId === ms.id);
                    const url = ans?.photoPath ? fullUrls[ans.photoPath] ?? photoUrls[ans.photoPath] : undefined;
                    return (
                      <li key={ms.id}>
                        <h4>
                          {i + 1}. {ms.title || '인증 항목'}
                        </h4>
                        {ans?.photoPath ? (
                          url ? (
                            <div className="cp-chl-subs-photo">
                              <img src={url} alt="" />
                            </div>
                          ) : (
                            <p className="cp-chl-subs-photo-loading">사진 불러오는 중…</p>
                          )
                        ) : null}
                        {ans?.text ? <p className="cp-chl-subs-text">{ans.text}</p> : null}
                        {!ans?.photoPath && !ans?.text ? (
                          <p className="cp-chl-subs-photo-loading">제출 내용 없음</p>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>,
            document.body
          )
        : null}


      {resetOpen
        ? createPortal(
            <div className="cp-modal-backdrop cp-chl-confirm-backdrop" role="presentation" onClick={() => setResetOpen(false)}>
              <div
                className="cp-modal cp-leave-modal-box"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="cp-chl-check-reset-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 id="cp-chl-check-reset-title" className="cp-leave-modal-title">
                  「{subTaskTitle}」 기록 초기화
                </h3>
                <p className="cp-leave-modal-desc">
                  이 챌린지의 제출 기록 {subs.length}건과 사진을 삭제하고, 학생들의 과제 상태(확인·제출 등)와 포인트도
                  모두 미확인으로 되돌립니다. 메모는 남아요. 되돌릴 수 없어요.
                  <br />
                  계속하려면 <b>초기화</b>를 입력해 주세요.
                </p>
                <input
                  className="cp-chl-reset-input"
                  value={resetText}
                  onChange={(e) => setResetText(e.target.value)}
                  placeholder="초기화"
                  aria-label="초기화 확인 문구"
                  autoFocus
                />
                <div className="cp-modal-footer cp-leave-modal-actions">
                  <button type="button" className="cp-btn-secondary" onClick={() => setResetOpen(false)} disabled={busy}>
                    취소
                  </button>
                  <button
                    type="button"
                    className="cp-btn-primary cp-chl-danger-btn"
                    onClick={() => void reset()}
                    disabled={busy || resetText.trim() !== '초기화'}
                  >
                    {busy ? '삭제 중…' : '초기화'}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ChallengeCheckStudentList;
